import { useMutation } from '@apollo/client'
import { useState, useCallback } from 'react'
import { APPLY_BEST_PRACTICES } from '../../../../../api/graphQL/mutations/bestPractices.mutations'
import {
    BP_APPLICATION_STATES,
    BP_SUCCESS_MESSAGE,
    ENABLE_SMART_PROJECT_COPY,
} from '../constants'
import {
    ApplyBestPracticesInput,
    ApplyBestPracticesResponse,
    BPApplicationStatus,
    UseBPApplicationResult,
} from '../types'

/**
 * Hook for managing Best Practice application workflow.
 * Handles application state and completion.
 *
 * @returns {UseBPApplicationResult} Application state and control functions
 *
 * Usage:
 * ```typescript
 * const { applicationStatus, applyBestPractices, isApplying } = useBPApplication()
 *
 * const handleApplyBP = async () => {
 *     await applyBestPractices({
 *         projectId: 'project-123',
 *         sourceProjectId: 'source-project-456',
 *         treePath: 'Division/Family'
 *     })
 * }
 *
 * // Render loading screen
 * <BPApplicationLoadingScreen
 *     open={isApplying}
 *     message={applicationStatus.message}
 * />
 * ```
 *
 * Related PRs: 174073, 174074, 176230
 */
export const useBPApplication = (): UseBPApplicationResult => {
    const [applicationStatus, setApplicationStatus] = useState<BPApplicationStatus>({
        state: BP_APPLICATION_STATES.IDLE,
        progress: 0,
        message: '',
        error: null,
    })

    const [applyBestPracticesMutation, { loading }] = useMutation<ApplyBestPracticesResponse>(
        APPLY_BEST_PRACTICES,
        {
            onCompleted: (data) => {
                const result = data.applyBestPractices

                if (result.success) {
                    if (result.clashesDetected > 0) {
                        setApplicationStatus({
                            state: BP_APPLICATION_STATES.CLASHES_DETECTED,
                            progress: 100,
                            message: `${result.message} ${result.clashesDetected} clash${
                                result.clashesDetected !== 1 ? 'es' : ''
                            } detected.`,
                            error: null,
                        })
                    } else {
                        setApplicationStatus({
                            state: BP_APPLICATION_STATES.SUCCESS,
                            progress: 100,
                            message: BP_SUCCESS_MESSAGE,
                            error: null,
                        })
                    }
                } else {
                    setApplicationStatus({
                        state: BP_APPLICATION_STATES.ERROR,
                        progress: 0,
                        message: result.message || 'Failed to apply Best Practices',
                        error: result.message,
                    })
                }
            },
            onError: (error) => {
                setApplicationStatus({
                    state: BP_APPLICATION_STATES.ERROR,
                    progress: 0,
                    message: 'An error occurred while applying Best Practices',
                    error: error.message,
                })
            },
        }
    )

    const applyBestPractices = useCallback(
        async (input: ApplyBestPracticesInput) => {
            if (!ENABLE_SMART_PROJECT_COPY) {
                console.warn('Smart Project Copy feature is not enabled')
                setApplicationStatus({
                    state: BP_APPLICATION_STATES.ERROR,
                    progress: 0,
                    message: 'Smart Project Copy feature is not available in your environment.',
                    error: 'Feature not enabled',
                })
                return
            }

            // Set applying state with simple loading message
            setApplicationStatus({
                state: BP_APPLICATION_STATES.APPLYING,
                progress: 0,
                message: 'Applying Best Practices...',
                error: null,
            })

            try {
                // Execute mutation
                await applyBestPracticesMutation({
                    variables: { input },
                })
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
                console.error('Error applying Best Practices:', error)

                setApplicationStatus({
                    state: BP_APPLICATION_STATES.ERROR,
                    progress: 0,
                    message: 'Failed to apply Best Practices. Please try again.',
                    error: errorMessage,
                })
            }
        },
        [applyBestPracticesMutation]
    )

    const resetApplication = useCallback(() => {
        setApplicationStatus({
            state: BP_APPLICATION_STATES.IDLE,
            progress: 0,
            message: '',
            error: null,
        })
    }, [])

    return {
        applicationStatus,
        applyBestPractices,
        resetApplication,
        isApplying: applicationStatus.state === BP_APPLICATION_STATES.APPLYING,
    }
}
